import React, { useState, useEffect } from 'react';
import { 
    Box, Typography, Button, Paper, Table, TableBody, TableCell, 
    TableContainer, TableHead, TableRow, IconButton, Chip, Tooltip,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions 
} from '@mui/material';
import { Add, ContentCopy, Visibility, Delete } from '@mui/icons-material'; // Added Delete Icon
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config'; 

const SurveyManagementPage = () => {
    const [surveys, setSurveys] = useState([]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [surveyToDelete, setSurveyToDelete] = useState(null);
    const navigate = useNavigate();

    // ... (fetchSurveys code remains the same) ...
    const fetchSurveys = async () => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/Surveys`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setSurveys(data);
            }
        } catch (error) {
            console.error("Error fetching surveys:", error);
        }
    };

    useEffect(() => {
        fetchSurveys();
    }, []);

    // NEW: Handle Delete Logic
    const handleDeleteClick = (survey) => {
        setSurveyToDelete(survey);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!surveyToDelete) return;

        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/Surveys/${surveyToDelete.id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                // Remove from UI immediately
                setSurveys(surveys.filter(s => s.id !== surveyToDelete.id));
                setDeleteDialogOpen(false);
                setSurveyToDelete(null);
            } else {
                alert("Failed to delete survey. Please try again.");
            }
        } catch (error) {
            console.error("Error deleting survey:", error);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        alert("Link copied to clipboard!");
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>My Surveys</Typography>
                <Button 
                    variant="contained" 
                    startIcon={<Add />}
                    onClick={() => navigate('/survey-management/new')}
                    sx={{ bgcolor: '#a8851d', '&:hover': { bgcolor: '#8c6e18' } }}
                >
                    Create Survey
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Project Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Created</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Link</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Responses</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {surveys.map((survey) => (
                            <TableRow key={survey.id}>
                                <TableCell>{survey.projectName || survey.title}</TableCell>
                                <TableCell>{new Date(survey.createdAt).toLocaleDateString()}</TableCell>
                                <TableCell>
                                    <Button
                                        size="small"
                                        startIcon={<ContentCopy />}
                                        onClick={() => copyToClipboard(`${window.location.origin}/survey/public/${survey.publicToken}`)}
                                    >
                                        Copy Link
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Chip label={survey.responseCount || 0} size="small" />
                                </TableCell>
                                <TableCell align="right">
                                    <Tooltip title="View Responses">
                                        <IconButton 
                                            color="primary"
                                            onClick={() => navigate(`/dashboard/survey-management/${survey.id}/responses`)}
                                        >
                                            <Visibility />
                                        </IconButton>
                                    </Tooltip>
                                    
                                    {/* NEW: Delete Button */}
                                    <Tooltip title="Delete Survey">
                                        <IconButton 
                                            color="error"
                                            onClick={() => handleDeleteClick(survey)}
                                        >
                                            <Delete />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
            >
                <DialogTitle>Delete Survey?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete <strong>{surveyToDelete?.projectName}</strong>? 
                        This will also delete all customer responses and photos associated with it. 
                        This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleConfirmDelete} color="error" variant="contained">
                        Delete Forever
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SurveyManagementPage;